import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabaseClient';

export interface DeviceToken {
  id?: string;
  user_id: string;
  device_token: string;
  platform: 'ios' | 'android' | 'web';
  created_at?: string;
  updated_at?: string;
  is_active: boolean;
}

export interface NotificationPayload {
  title: string;
  body: string;
  data?: Record<string, any>;
}

class PushNotificationService {
  private static instance: PushNotificationService;
  private isInitialized = false;
  private currentToken: string | null = null;

  private constructor() {}

  static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('[PushNotifications] Already initialized');
      return;
    }

    const platform = Capacitor.getPlatform();
    if (platform === 'web') {
      console.log('[PushNotifications] Web platform - skipping native push setup');
      return;
    }

    try {
      console.log('[PushNotifications] 🚀 Initializing push notifications...');

      // Request permission
      const permStatus = await PushNotifications.requestPermissions();
      console.log('[PushNotifications] Permission status:', permStatus);

      if (permStatus.receive === 'granted') {
        // Register for push notifications
        await PushNotifications.register();
        console.log('[PushNotifications] ✅ Registered for push notifications');
      } else {
        console.warn('[PushNotifications] ⚠️ Push notification permission denied');
        return;
      }

      // Listen for registration success
      await PushNotifications.addListener('registration', async (token) => {
        console.log('[PushNotifications] 📱 Device token received:', token.value);
        this.currentToken = token.value;
        await this.saveDeviceToken(token.value);
      });

      // Listen for registration errors
      await PushNotifications.addListener('registrationError', (error) => {
        console.error('[PushNotifications] ❌ Registration error:', error);
      });

      // Listen for push notifications received
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[PushNotifications] 📩 Push notification received:', notification);
        // Show local notification if app is in foreground
        this.handleForegroundNotification(notification);
      });

      // Listen for notification actions (when user taps notification)
      await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('[PushNotifications] 👆 Notification action performed:', notification);
        this.handleNotificationAction(notification);
      });

      this.isInitialized = true;
      console.log('[PushNotifications] ✅ Initialization complete');
    } catch (error) {
      console.error('[PushNotifications] ❌ Initialization failed:', error);
      throw error;
    }
  }

  private async saveDeviceToken(token: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('[PushNotifications] No authenticated user');
        return;
      }

      const platform = Capacitor.getPlatform() as 'ios' | 'android' | 'web';

      // Check if token already exists for this user
      const { data: existing } = await supabase
        .from('device_tokens')
        .select('*')
        .eq('user_id', user.id)
        .eq('device_token', token)
        .single();

      if (existing) {
        // Update existing token
        await supabase
          .from('device_tokens')
          .update({
            is_active: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);
        console.log('[PushNotifications] ✅ Device token updated');
      } else {
        // Insert new token
        await supabase
          .from('device_tokens')
          .insert({
            user_id: user.id,
            device_token: token,
            platform,
            is_active: true
          });
        console.log('[PushNotifications] ✅ Device token saved');
      }
    } catch (error) {
      console.error('[PushNotifications] ❌ Failed to save device token:', error);
    }
  }

  private handleForegroundNotification(notification: any): void {
    // Display a local notification or update UI
    console.log('[PushNotifications] Handling foreground notification:', notification);
    
    // You can dispatch a custom event to update UI
    window.dispatchEvent(new CustomEvent('notification-received', {
      detail: notification
    }));
  }

  private handleNotificationAction(notification: any): void {
    console.log('[PushNotifications] Handling notification action:', notification);
    
    const data = notification.notification?.data;
    if (!data) return;

    // Route based on notification type
    switch (data.type) {
      case 'ai_chat':
        window.location.href = `/ai-chat?session=${data.sessionId}`;
        break;
      case 'team_chat':
        window.location.href = `/team-chat`;
        break;
      case 'calendar':
        window.location.href = `/calendar?event=${data.eventId}`;
        break;
      default:
        console.log('[PushNotifications] Unknown notification type:', data.type);
    }
  }

  async unregister(): Promise<void> {
    if (!this.isInitialized) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && this.currentToken) {
        // Mark device token as inactive
        await supabase
          .from('device_tokens')
          .update({ is_active: false })
          .eq('user_id', user.id)
          .eq('device_token', this.currentToken);
      }

      await PushNotifications.removeAllListeners();
      this.isInitialized = false;
      this.currentToken = null;
      console.log('[PushNotifications] ✅ Unregistered');
    } catch (error) {
      console.error('[PushNotifications] ❌ Failed to unregister:', error);
    }
  }

  async getDeviceToken(): Promise<string | null> {
    return this.currentToken;
  }

  isReady(): boolean {
    return this.isInitialized && this.currentToken !== null;
  }
}

export default PushNotificationService;
